import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, Phone, Pencil, X, Check, Camera, Loader2 } from "lucide-react";
import { externalSupabase } from "@/lib/externalSupabase";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface GuestProfile {
  id: string;
  email: string;
  full_name: string | null;
  phone_number: string | null;
  airbnb_profile_url: string | null;
  avatar_url: string | null;
  subscription_tier: string | null;
}

interface DashboardProfileProps {
  profile: GuestProfile;
  onProfileUpdate: (updated: GuestProfile) => void;
}

const tierConfig: Record<string, { label: string; className: string }> = {
  free: {
    label: "Free Tier",
    className: "bg-muted text-muted-foreground border-border",
  },
  pro: {
    label: "Pro Member",
    className: "bg-primary/10 text-primary border-primary/30",
  },
  expert: {
    label: "Expert Audit Active",
    className: "bg-chart-4/20 text-chart-4 border-chart-4/30",
  },
};

const DashboardProfile = ({ profile, onProfileUpdate }: DashboardProfileProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingAvatar, setFetchingAvatar] = useState(false);
  const [phone, setPhone] = useState(profile.phone_number || "");
  const [airbnbUrl, setAirbnbUrl] = useState(profile.airbnb_profile_url || "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tier = tierConfig[profile.subscription_tier || "free"] || tierConfig.free;
  const initials = profile.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : profile.email[0].toUpperCase();

  const uploadAvatarToStorage = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${profile.id}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      console.error("Upload error:", uploadError);
      return null;
    }

    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    return data.publicUrl + `?t=${Date.now()}`;
  };

  const saveAvatarUrl = async (url: string) => {
    console.log("[DashboardProfile] Saving avatar to DB:", url);
    console.log("[DashboardProfile] Target row id:", profile.id);

    const { data, error, count } = await externalSupabase
      .from("guests")
      .update({ avatar_url: url })
      .eq("id", profile.id)
      .select("id, avatar_url");

    if (error) {
      console.error("[DashboardProfile] Supabase Save Error:", error);
      toast({ title: "Failed to save avatar", description: error.message, variant: "destructive" });
      return false;
    }

    console.log("[DashboardProfile] Update response data:", data);

    if (!data || data.length === 0) {
      console.error("[DashboardProfile] Update returned 0 rows — likely blocked by RLS policy. The 'guests' table needs an UPDATE policy on the external database.");
      toast({
        title: "Avatar not saved",
        description: "Database rejected the update. The guests table may need an UPDATE policy.",
        variant: "destructive",
      });
      return false;
    }

    const confirmedUrl = data[0]?.avatar_url || url;
    console.log("[DashboardProfile] Avatar saved & confirmed:", confirmedUrl);
    onProfileUpdate({ ...profile, avatar_url: confirmedUrl });
    return true;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file.", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5MB.", variant: "destructive" });
      return;
    }

    setUploading(true);
    const url = await uploadAvatarToStorage(file);
    if (url) {
      await saveAvatarUrl(url);
      toast({ title: "Photo updated" });
    } else {
      toast({ title: "Upload failed", variant: "destructive" });
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFetchAirbnbAvatar = async (url: string) => {
    if (!url.trim() || !url.includes("airbnb")) return;

    setFetchingAvatar(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-airbnb-avatar", {
        body: { profileUrl: url.trim() },
      });

      if (error || !data?.success || !data?.avatarUrl) {
        console.log("Could not auto-fetch avatar:", error || data?.error);
        setFetchingAvatar(false);
        return;
      }

      await saveAvatarUrl(data.avatarUrl);
      toast({ title: "Profile photo fetched from Airbnb" });
    } catch (err) {
      console.error("Avatar fetch error:", err);
    }
    setFetchingAvatar(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await externalSupabase
      .from("guests")
      .update({
        phone_number: phone.trim() || null,
        airbnb_profile_url: airbnbUrl.trim() || null,
      })
      .eq("id", profile.id);

    setSaving(false);

    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }

    const updatedProfile = {
      ...profile,
      phone_number: phone.trim() || null,
      airbnb_profile_url: airbnbUrl.trim() || null,
    };
    onProfileUpdate(updatedProfile);
    setEditing(false);
    toast({ title: "Profile updated" });

    // Auto-fetch avatar if Airbnb URL was added/changed and no avatar exists
    if (airbnbUrl.trim() && airbnbUrl.trim() !== profile.airbnb_profile_url && !profile.avatar_url) {
      handleFetchAirbnbAvatar(airbnbUrl.trim());
    }
  };

  const handleCancel = () => {
    setPhone(profile.phone_number || "");
    setAirbnbUrl(profile.airbnb_profile_url || "");
    setEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="rounded-2xl border bg-card p-6 mb-8"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {/* Avatar with upload overlay */}
        <div className="relative group">
          <Avatar className="h-16 w-16 ring-2 ring-border">
            {profile.avatar_url ? (
              <AvatarImage src={profile.avatar_url} alt={profile.full_name || "Avatar"} />
            ) : null}
            <AvatarFallback className="bg-accent text-accent-foreground text-lg font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>

          {/* Upload overlay */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || fetchingAvatar}
            className="absolute inset-0 rounded-full bg-foreground/60 text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-wait"
            title="Upload photo"
          >
            {uploading || fetchingAvatar ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-lg font-semibold text-foreground truncate">
              {profile.full_name || profile.email}
            </h2>
            <Badge className={`text-xs border ${tier.className}`}>
              {tier.label}
            </Badge>
            {fetchingAvatar && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Fetching photo…
              </span>
            )}
          </div>

          {!editing ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-muted-foreground">
              <span>{profile.email}</span>
              {profile.phone_number && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  {profile.phone_number}
                </span>
              )}
              {profile.airbnb_profile_url && (
                <a
                  href={profile.airbnb_profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Airbnb Profile
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-3 pt-1 max-w-md">
              <div>
                <Label htmlFor="phone" className="text-xs text-muted-foreground">Phone Number</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555-123-4567"
                  className="mt-1 h-9"
                />
              </div>
              <div>
                <Label htmlFor="airbnb" className="text-xs text-muted-foreground">Airbnb Profile URL</Label>
                <Input
                  id="airbnb"
                  value={airbnbUrl}
                  onChange={(e) => setAirbnbUrl(e.target.value)}
                  placeholder="https://airbnb.com/users/show/..."
                  className="mt-1 h-9"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your profile photo will be fetched automatically when you save.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                  <Check className="w-3.5 h-3.5" />
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancel} className="gap-1.5">
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Edit button */}
        {!editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            className="gap-1.5 shrink-0"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit Profile
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default DashboardProfile;
